import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import {
  ChartFacade,
  ChartId,
} from '@principle-theorem/ng-clinical-charting/store';
import {
  PipesModule,
  TrackByFunctions,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import {
  ClinicalChart,
  MockMolar,
  toAllChartedRefs,
} from '@principle-theorem/principle-core';
import {
  CHARTABLE_SURFACES,
  ChartableSurface,
  ChartView,
  IChartedRef,
  ITooth,
  Quadrant,
} from '@principle-theorem/principle-core/interfaces';
import { getEnumValues, isChanged$, snapshot } from '@principle-theorem/shared';
import { compact, difference } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  type Observable,
  ReplaySubject,
} from 'rxjs';
import { map, startWith, tap, withLatestFrom } from 'rxjs/operators';
import { ClinicalChartComponent } from '../../clinical-chart/clinical-chart.component';
import { CHART_ENTITY_ID } from '../../dental-chart-svg/chart-entity-id';
import {
  AvailableSurfacesFormGroup,
  getSelectedSurfaces,
  setSelectedSurfaces,
} from './available-surfaces.formgroup';

@Component({
  selector: 'pr-available-surfaces',
  templateUrl: './available-surfaces.component.html',
  styleUrls: ['./available-surfaces.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: CHART_ENTITY_ID,
      useValue: ChartId.ChartableSurfaces,
    },
  ],
  imports: [
    CommonModule,
    MatCheckbox,
    MatIcon,
    PipesModule,
    ReactiveFormsModule,
    forwardRef(() => ClinicalChartComponent),
  ],
})
export class AvailableSurfacesComponent implements OnDestroy {
  private readonly _defaultTooth = MockMolar(Quadrant.AdultUpperRight, 1);
  private _surfaces$ = new ReplaySubject<ChartableSurface[]>(1);
  private _chartId: ChartId = inject(CHART_ENTITY_ID);
  private _previousView?: ChartView;
  trackBySurface = TrackByFunctions.variable<ChartableSurface>();
  form = new AvailableSurfacesFormGroup();
  @Output() selectedSurfaces = new EventEmitter<ChartableSurface[]>();
  isUnscoped$: Observable<boolean>;

  tooth$ = new ReplaySubject<ITooth>(1);
  selectableSurfaces$ = new BehaviorSubject<ChartableSurface[]>(
    CHARTABLE_SURFACES.sort()
  );

  @Input()
  set selectableSurfaces(selectableSurfaces: ChartableSurface[]) {
    if (selectableSurfaces) {
      this.selectableSurfaces$.next(selectableSurfaces.sort());

      const disabledSurfaces = difference(
        getEnumValues(ChartableSurface),
        selectableSurfaces
      );
      this._chartStore.setDisabledSurfaces(this._chartId, disabledSurfaces);
    }
  }

  @Input()
  set tooth(tooth: ITooth) {
    if (tooth) {
      this.tooth$.next(tooth);
    }
  }

  @Input()
  set surfaces(surfaces: ChartableSurface[]) {
    if (surfaces) {
      this._surfaces$.next(surfaces);
    }
  }

  constructor(private _chartStore: ChartFacade) {
    this.tooth$
      .pipe(
        startWith(this._defaultTooth),
        withLatestFrom(
          this._chartStore.chartView$(this._chartId).pipe(startWith(undefined))
        ),
        takeUntilDestroyed()
      )
      .subscribe(([tooth, previousView]) => {
        this._previousView = previousView;

        const chart = ClinicalChart.init({
          teeth: [
            {
              ...tooth,
              toothRef: {
                quadrant: tooth.toothRef.quadrant,
                quadrantIndex: 1,
              },
            },
          ],
        });
        this._chartStore.setChartView(this._chartId, ChartView.All);
        this._chartStore.loadChartSuccess(this._chartId, chart);
        this._chartStore.setChartTeeth(this._chartId, chart.teeth);
      });

    this._formChanged$()
      .pipe(takeUntilDestroyed())
      .subscribe((surfaces) => this._surfaces$.next(surfaces));

    this._surfaces$
      .pipe(
        isChanged$(),
        tap((surfaces) => void this._updateChartSelected(surfaces)),
        tap((surfaces) => setSelectedSurfaces(this.form, surfaces)),
        takeUntilDestroyed()
      )
      .subscribe((surfaces) => this.selectedSurfaces.emit(surfaces));

    this._chartStore
      .selectedSurfacesState$(this._chartId)
      .pipe(
        map((selectedSurfaces) =>
          compact(
            selectedSurfaces
              .map((selectedSurface) =>
                this._determineSelectedSurface(selectedSurface)
              )
              .flat()
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe((selectedSurfaces) => {
        const surfacesMap = selectedSurfaces.reduce(
          (formData, selectedSurface) => ({
            ...formData,
            [selectedSurface]: true,
          }),
          new AvailableSurfacesFormGroup().getRawValue()
        );

        this.form.patchValue(surfacesMap, {
          emitEvent: false,
        });
        this.selectedSurfaces.emit(selectedSurfaces);
      });

    this.isUnscoped$ = combineLatest([
      this.selectableSurfaces$,
      this._surfaces$,
    ]).pipe(
      map(
        ([selectableSurfaces, surfaces]) =>
          selectableSurfaces.includes(ChartableSurface.Unscoped) &&
          (!surfaces.length || surfaces.includes(ChartableSurface.Unscoped))
      )
    );
  }

  ngOnDestroy(): void {
    this._chartStore.setChartTeeth(this._chartId, [this._defaultTooth]);
    this._chartStore.setDisabledSurfaces(this._chartId, []);
    if (this._previousView) {
      this._chartStore.setChartView(this._chartId, this._previousView);
    }
  }

  private _formChanged$(): Observable<ChartableSurface[]> {
    return validFormGroupChanges$(this.form).pipe(
      map((data) => getSelectedSurfaces(data))
    );
  }

  private async _updateChartSelected(
    selected: ChartableSurface[]
  ): Promise<void> {
    const chart = await snapshot(
      this._chartStore.clinicalChartState$(this._chartId)
    );
    this._chartStore.setSelectedSurfaces(
      this._chartId,
      toAllChartedRefs(selected, chart)
    );
  }

  private _determineSelectedSurface(
    selectedSurface: Partial<IChartedRef>
  ): ChartableSurface | undefined {
    if (selectedSurface.arch) {
      return ChartableSurface.Arch;
    }

    if (selectedSurface.multipleTeeth) {
      return ChartableSurface.MultipleTeeth;
    }

    if (selectedSurface.quadrant) {
      return ChartableSurface.Quadrant;
    }

    if (selectedSurface.tooth) {
      return selectedSurface.tooth.surface || ChartableSurface.WholeTooth;
    }

    if (selectedSurface.unscoped) {
      return ChartableSurface.Unscoped;
    }

    if (selectedSurface.wholeMouth) {
      return ChartableSurface.WholeMouth;
    }
  }
}
